import QueryParamsUtil from 'api/utils/query-params-util'
import axios from 'axios'

const API_PATH = 'api/v2/admins/inventories'

const InventoriesApiV2 = {
  get: async ({ tableState, additionalFilters } = {}) => {
    const params   = QueryParamsUtil.format(tableState, additionalFilters)
    const response = axios.get(API_PATH, { params })
    return response
  },

  show: async (id) => {
    const path     = `${API_PATH}/${id}`
    const response = axios.get(path)
    return response
  },

  cancelConsign: async (id, note, physicalAbsent) => axios.post(`${API_PATH}/${id}/cancel_consign`, { note, physical_absent: physicalAbsent }),

  editOptions: async () => {
    const path     = `${API_PATH}/edit_options`
    const response = axios.get(path)
    return response
  },

  updateProduct: async (inventory_id, params, options = {}) => {
    const path    = `${API_PATH}/${inventory_id}/update_product`
    const payload = new FormData()
    const fileFields = [
      'front_view',
      'right_side_view',
      'rear_view',
      'left_side_view',
      'top_view',
      'bottom_view',
      'photo_details',
      'comes_with_image',
      'stamp_image',
      'card_image',
      'insole_photo',
      'authentication_certificate'
    ]
    const skippedProductFields = [
      'sellable_quantity',
      'inventory_quantity'
    ]

    if (options.confirmLowSellingPrice) payload.append('confirm_low_selling_price', '1')

    Object.keys(params || {}).forEach((key) => {
      if (skippedProductFields.includes(key)) return

      const value = params[key]

      if (fileFields.includes(key)) {
        if (Array.isArray(value)) {
          value.forEach((file) => {
            if (file) payload.append(`product[${key}][]`, file)
          })
        }
        else if (value) {
          payload.append(`product[${key}]`, value)
        }

        return
      }

      if (Array.isArray(value)) {
        value.forEach((item) => payload.append(`product[${key}][]`, item))
        return
      }

      if (value !== undefined && value !== null) {
        payload.append(`product[${key}]`, value)
      }
    })

    const response = axios.patch(path, payload)
    return response
  },

  deletePhoto: async (inventory_id, attachment_id) => {
    const path = `${API_PATH}/${inventory_id}/delete_photo`
    const response = axios.delete(path, {
      data: { attachment_id }
    })
    return response
  },

  manualDelete: async ({ inventory_id, reason, note }) => {
    const path = `${API_PATH}/${inventory_id}/manual_delete`
    const response = axios.post(path, {
      manual_delete: {
        reason: reason,
        note: note
      }
    })
    return response
  },

  adjustSellableQuantity: async ({ inventory_id, action, reason }) => {
    const path = `${API_PATH}/${inventory_id}/adjust_sellable_quantity`
    return axios.post(path, { sellable_adjustment: { action, reason } })
  },

  undoDelete: async ({ inventory_id }) => {
    const path = `${API_PATH}/${inventory_id}/undo_delete`
    const response = axios.post(path)
    return response
  },

  markSoldOnHold: async ({ inventory_id, action }) => {
    const path = `${API_PATH}/${inventory_id}/mark_sold_on_hold`
    const response = axios.post(path, {
      mark_sold_on_hold: { action }
    })
    return response
  },

  bulkMarkSoldOnHold: async ({ sku_list, action }) => {
    const path = `${API_PATH}/bulk_mark_sold_on_hold`
    const response = axios.post(path, {
      bulk_mark_sold_on_hold: { sku_list, action }
    })
    return response
  },

  updateLocation: async ({
    inventory_id,
    old_location_id,
    new_location_id,
    quantity
  }) => {
    const endpoint = `${API_PATH}/${inventory_id}/update_location`
    const payload  = {
      'inventory': {
        'old_location_id' : old_location_id,
        'new_location_id' : new_location_id,
        'quantity'        : quantity
      }
    }

    const response = axios.patch(endpoint, payload)
    return response
  },

  /**
   * Update inventory quantity and create inventory movement
   * Invoke `POST api/v2/admins/inventories/:id/fix_data
   */
  fixData: async (params, inventory_id) => {
    const path     = `${API_PATH}/${inventory_id}/fix_data`
    const payload  = {
      inventory_movement: params
    }
    const response = axios.post(path, payload)
    return response
  },

  /**
   * Get list of Inventoriess using csv
   * Invoke `POST api/v2/admins/inventories/csv
   * @param {BinaryType} file - csv file
   */
  indexCsv: async (file, options = {}) => {
    const path  = `${API_PATH}/index_csv`
    let payload = new FormData()
    payload.append('file', file)
    if (options.eligible_for_seller_return) payload.append('eligible_for_seller_return', 'true')
    if (options.eligible_for_internal_request) payload.append('eligible_for_internal_request', 'true')

    const response = await axios.post(path, payload)
    return response
  },

  previewCostCsv: async (file, options = {}) => {
    const path  = `${API_PATH}/preview_cost_csv`
    let payload = new FormData()

    payload.append('file', file)
    if (options.force) payload.append('force', 'true')

    const response = await axios.post(path, payload)
    return response
  },

  importCostCsv: async (file, options = {}) => {
    const path  = `${API_PATH}/import_cost_csv`
    let payload = new FormData()

    payload.append('file', file)
    if (options.force) payload.append('force', 'true')

    const response = await axios.post(path, payload, { responseType: 'blob' })
    return response
  },

  search: async (keyword = '', options = {}) => {
    const params = {
      q        : keyword,
      per_page : options.per_page || 10,
      page     : options.page || 1
    }

    if (options.seller_id) params.seller_id = options.seller_id
    if (options.product_status) params.product_status = options.product_status
    if (options.eligible_for_seller_return) params.eligible_for_seller_return = 'true'
    if (options.eligible_for_internal_request) params.eligible_for_internal_request = 'true'
    if (options.eligible_for_cash_handover) params.eligible_for_cash_handover = 'true'
    if (options.show_all_inventory) params.show_all_inventory = 'true'

    const response = axios.get(API_PATH, { params })
    return response
  }
}

export default InventoriesApiV2
